import React, { memo } from "react";
import { Pressable, StyleSheet, View } from "react-native";
import Text from "@components/CustomText";
import {
  HISTORY_COLORS,
  WEEKDAY_LABELS,
  buildMonthGrid,
  canGoNextMonth,
  canGoPrevMonth,
  formatMonthTitle,
  statusStyle,
} from "@utils/attendanceCalendar";
import { ATTENDANCE_COLORS as C } from "./constants";
import type { HistoryDay } from "./types";

interface Props {
  month: string;
  days: HistoryDay[];
  todayDate: string;
  selectedDate: string | null;
  onSelectDate: (date: string) => void;
  onPrevMonth: () => void;
  onNextMonth: () => void;
}

interface GridCell {
  date: string;
  day: number;
}

const CELL_PERCENT = `${100 / 7}%` as const;

const HistoryCalendar = ({
  month,
  days,
  todayDate,
  selectedDate,
  onSelectDate,
  onPrevMonth,
  onNextMonth,
}: Props) => {
  const statusByDate: Record<string, HistoryDay> = {};
  days.forEach((d) => {
    statusByDate[d.date] = d;
  });
  const canPrev = canGoPrevMonth(month);
  const canNext = canGoNextMonth(month);

  return (
    <View style={styles.card} testID="attendance-history-calendar">
      <View style={styles.monthRow}>
        <Pressable
          onPress={onPrevMonth}
          disabled={!canPrev}
          hitSlop={10}
          style={[styles.arrow, !canPrev && styles.arrowDisabled]}
          accessibilityRole="button"
          accessibilityLabel="Previous month"
          accessibilityState={{ disabled: !canPrev }}
          testID="attendance-history-prev"
        >
          <Text style={styles.arrowText}>‹</Text>
        </Pressable>
        <Text style={styles.monthTitle} testID="attendance-history-month">
          {formatMonthTitle(month)}
        </Text>
        <Pressable
          onPress={onNextMonth}
          disabled={!canNext}
          hitSlop={10}
          style={[styles.arrow, !canNext && styles.arrowDisabled]}
          accessibilityRole="button"
          accessibilityLabel="Next month"
          accessibilityState={{ disabled: !canNext }}
          testID="attendance-history-next"
        >
          <Text style={styles.arrowText}>›</Text>
        </Pressable>
      </View>
      <View style={styles.row}>
        {WEEKDAY_LABELS.map((label, i) => (
          <View key={`${label}-${i}`} style={styles.cellSlot}>
            <Text style={styles.weekday}>{label}</Text>
          </View>
        ))}
      </View>
      <View style={styles.row}>
        {(buildMonthGrid(month) as (GridCell | null)[]).map((cell, i) => {
          if (!cell) return <View key={`blank-${i}`} style={styles.cellSlot} />;
          const entry = statusByDate[cell.date];
          const look = statusStyle(entry?.status);
          const isToday = cell.date === todayDate;
          const isSelected = cell.date === selectedDate;
          return (
            <View key={cell.date} style={styles.cellSlot}>
              <Pressable
                onPress={() => onSelectDate(cell.date)}
                style={[
                  styles.day,
                  {
                    backgroundColor: look.backgroundColor,
                    borderColor: isToday
                      ? HISTORY_COLORS.todayRing
                      : look.borderColor,
                  },
                  isToday && styles.dayToday,
                  isSelected && styles.daySelected,
                ]}
                accessibilityRole="button"
                accessibilityLabel={`${cell.date}${entry ? `, ${entry.status}` : ""}`}
                accessibilityState={{ selected: isSelected }}
                testID={`attendance-history-day-${cell.date}`}
              >
                <Text style={[styles.dayText, { color: look.textColor }]}>
                  {cell.day}
                </Text>
              </Pressable>
            </View>
          );
        })}
      </View>
      <View style={styles.legend}>
        <LegendItem color={HISTORY_COLORS.complete} label="In & Out" />
        <LegendItem color={HISTORY_COLORS.inOnly} label="In only" />
        <LegendItem color={HISTORY_COLORS.absent} label="Not marked" />
      </View>
    </View>
  );
};

const LegendItem = ({ color, label }: { color: string; label: string }) => (
  <View style={styles.legendItem}>
    <View style={[styles.legendDot, { backgroundColor: color }]} />
    <Text style={styles.legendText}>{label}</Text>
  </View>
);

export default memo(HistoryCalendar);

const styles = StyleSheet.create({
  card: { backgroundColor: C.white, borderRadius: 14, padding: 12 },
  monthRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 8,
  },
  monthTitle: { color: C.ink, fontSize: 16, fontWeight: "700" },
  arrow: {
    width: 36,
    height: 36,
    borderRadius: 18,
    alignItems: "center",
    justifyContent: "center",
  },
  arrowDisabled: { opacity: 0.3 },
  arrowText: { color: C.ink, fontSize: 24, lineHeight: 28 },
  row: { flexDirection: "row", flexWrap: "wrap" },
  cellSlot: {
    width: CELL_PERCENT,
    alignItems: "center",
    paddingVertical: 3,
  },
  weekday: { color: C.label, fontSize: 12, fontWeight: "600" },
  day: {
    width: 34,
    height: 34,
    borderRadius: 8,
    borderWidth: 1,
    alignItems: "center",
    justifyContent: "center",
  },
  dayToday: { borderWidth: 2 },
  daySelected: { transform: [{ scale: 1.08 }] },
  dayText: { fontSize: 13, fontWeight: "600" },
  legend: {
    flexDirection: "row",
    justifyContent: "space-around",
    marginTop: 12,
  },
  legendItem: { flexDirection: "row", alignItems: "center", gap: 6 },
  legendDot: { width: 10, height: 10, borderRadius: 3 },
  legendText: { color: C.muted, fontSize: 12 },
});
